import React = require('react');
import { differenceInDays, parse } from 'date-fns';
import { Field, InjectedFormProps } from 'redux-form';
import { IChannel, IOptionMap, IPremium, IProduct } from '../../../../../data/api/models';
import { II18nMessages } from '../../../../../services/branding/models';
import { find, get, includes, keys, map } from 'lodash';
import { ButtonAsRadio } from '../../../../../components/ButtonRadioStyle';
import { Collapsable, ICollapsableComponent, IQuoteState } from '../../../../../services/quote/models';
import { useEffect, useState } from 'react';
import { Modal as BootstrapModal } from 'react-bootstrap/lib';
import { Button, Col, ModalBody, ModalHeader, ModalTitle, Row } from 'react-bootstrap';
import * as Commonmark from 'react-commonmark';
import { replaceBenefitTextPlaceHolder } from '../../../../../helpers/Quote';
import QuoteIcons from '../Icon/QuoteIcons';
import { setCollapsable } from '../../../../../services/quote/reducer';
import { AdmiralCardV2 } from '../AdmiralCardV2';
import { Info } from '../../../components/Info';

interface IAdditionalCoverProps extends InjectedFormProps {
  options: IOptionMap,
  i18n: II18nMessages;
  synchronousError: any;
  selectedPremium: IPremium;
  quoteState: IQuoteState;
  recalculate: () => any;
  product: IProduct;
  channel: IChannel;
  startDate: string;
  endDate: string;
  tripType: string;
  valid: boolean;
  collapsable?: ICollapsableComponent;
  hideContinueButton?: boolean

  renderDefinition(name: string, useProvidedString?: boolean): any;
}

export const AdditionalCover = ({
                                  selectedPremium,
                                  recalculate,
                                  quoteState,
                                  product,
                                  options,
                                  channel,
                                  change,
                                  startDate,
                                  endDate,
                                  tripType,
                                  valid,
                                  collapsable,
                                  hideContinueButton = false,
                                  ...props
                                }: IAdditionalCoverProps) => {
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [selectedOption, setSelectedOption] = useState({ name: null, key: null });
  const [showWinterSport, setShowWinterSport] = useState(true);

  const hasWinterSportOption = () => {
    const winterSports = get(boltOns, 'winter_sports')
    if (get(winterSports, 'total') === 0) {
      setShowWinterSport(false);
    }
  }

  const canShowWinterSports = () => {
    const MAX_DAYS_WINTER = 21;
    const isSingle = tripType === 'single'
    const start = parse(startDate, 'dd/MM/yyyy', new Date());
    const end = parse(endDate, 'dd/MM/yyyy', new Date());
    const duration = differenceInDays(end, start);
    if (isSingle && duration > MAX_DAYS_WINTER) {
      setShowWinterSport(false);
    } else {
      setShowWinterSport(true)
    }
  }

  useEffect(() => {
    if (selectedPremium) {
      hasWinterSportOption();
      canShowWinterSports();
    }
  }, [selectedPremium]);

  const boltOns = get(selectedPremium, 'information.boltOns')

  const handleOptionAdd = () => () => {
    recalculate()
  }

  const closeOptionModal = () => {
    setShowOptionsModal(false);
  }

  const handleOptionRemoveModal = (optionName, optionKey) => () => {
    setSelectedOption({ name: optionName, key: optionKey })
    setShowOptionsModal(true);
  }

  const handleOptionRemove = (optionKey) => () => {
    change(`options.${optionKey}`, false);
    recalculate()
    setShowOptionsModal(false);
  }

  const renderOptions = () => {
    return (
      map(boltOns, (value, key) => {
        const isSelected = get(options, key)
        const channelType = channel.channelType;
        const aggregatorChannel = channelType === 'AGG';
        const isAggregatorCruiseCover = key === 'aggregator_cruise_cover';
        const hasAggregatorCruiseCoverBenefit = includes(keys(boltOns), 'aggregator_cruise_cover');
        const isCruiseCover = key === 'cruise_cover';
        let text = null;

        if (!showWinterSport && key === 'winter_sports') {
          return null;
        }

        if (key === 'hazardous_activities') {
          return null;
        }

        if (value.total === 0) {
          return null;
        }

        if (!aggregatorChannel && isAggregatorCruiseCover) {
          return null;
        }

        if (
          aggregatorChannel
          && (
            (hasAggregatorCruiseCoverBenefit && isCruiseCover)
            || (!hasAggregatorCruiseCoverBenefit && isAggregatorCruiseCover)
          )
        ) {
          return null;
        }
        const newText = get(product, `metadata.definitions.${key}_alt_title_new`)

        text = newText ? newText : get(product, `metadata.definitions.${key}_alt_title`);
        const coverLevel = get(selectedPremium, 'scheme.coverLevel');

        if (key === 'winter_sports') {
          const wsBenefit = find(get(selectedPremium, 'information.allBenefits', []), ['name', 'Winter sports'])
          const skyEquipment = get(get(wsBenefit, 'sub_benefits', []).find((sb) => sb.name.includes('Ski equipment')), 'cover_level');
          if (get(skyEquipment, coverLevel, null)) {
            text = text.replace('{alt_value}', get(skyEquipment, coverLevel))
          }
        }

        return (
          <div className={'options-container'}>
            <div className={'option-icon'}><QuoteIcons name={key} color={isSelected ? 'green' : 'blue'}/></div>
            <div className={'option-description-section'}>
              <div className={isSelected ? 'option-name selected' : 'option-name'}>{value.name}</div>
              <div className={'option-description'}>
                <Commonmark
                  source={text}
                />
              </div>
            </div>
            <div className={'option-button-container'}>
              <div className={'option-total-value'}><strong>£{(value.total).toFixed(2)}</strong> total</div>
              <div>
                <Field
                  name={`options.${key}`}
                  simpleValue={true}
                  className={'admiral-white-btn'}
                  style={{ minWidth: '114px' }}
                  selectedText={'EDIT'}
                  unSelectedText={'ADD COVER'}
                  component={ButtonAsRadio}
                  disabled={quoteState.isCalculating || !valid}
                  handleChange={isSelected ? handleOptionRemoveModal(value.name, key) : handleOptionAdd()}
                />
              </div>
            </div>
          </div>
        )
      })
    )
  }

  const onChangeCollapse = () => {
    props.dispatch(setCollapsable(null, Collapsable.ADDITIONAL));
  }

  const handleContinue = () => {
    props.dispatch(setCollapsable(Collapsable.ADDITIONAL, Collapsable.HAZARDOUS_ACTIVITIES));
  }

  const renderContinueButton = () => {

    if (hideContinueButton) {
      return null;
    }

    return (
      <Row style={{ padding: '15px' }}>
        <Col xs={12}>
          <Button
            bsSize={'small'}
            type="button"
            block={true}
            disabled={quoteState.isCalculating}
            onClick={handleContinue}
            className={'admiral-primary-btn'}>
            CONTINUE
          </Button>
        </Col>
      </Row>
    )
  }

  const renderModal = () => {
    let bodyText = '';
    let dynamicText = '';
    const coverLevel = get(selectedPremium, 'scheme.coverLevel');
    const benefits = get(selectedPremium, 'benefits');
    const isGolfCover = selectedOption.key === 'golf_cover';
    const isGadgetCover = selectedOption.key === 'gadget_cover';

    if (isGolfCover) {
      bodyText = product.metadata.definitions[`${selectedOption.key}_alt_title`];
      dynamicText = product.metadata.definitions[`${selectedOption.key}_alt_dynamic`];
    } else {
      bodyText = product.metadata.definitions[`${selectedOption.key}_alt_${coverLevel}`];
      dynamicText = product.metadata.definitions[`${selectedOption.key}_alt_dynamic`];
      if (isGadgetCover) {
        dynamicText = product.metadata.definitions[`${selectedOption.key}_alt_dynamic_new`];
        bodyText = '';
      }
    }

    dynamicText = replaceBenefitTextPlaceHolder(dynamicText, benefits, selectedOption.key, false, coverLevel)

    return (
      <BootstrapModal
        show={showOptionsModal}
        onHide={closeOptionModal}
        keyboard={false}
        animation={true}
        backdrop="static"
      >
        <ModalHeader closeButton={true}>
          <ModalTitle><img className={'modal-title-icon'}
                           src={selectedOption.key ? require(`../../../img/${selectedOption.key}.svg`) : ''}
                           alt={'Admiral'}/>{selectedOption.name}</ModalTitle>
        </ModalHeader>
        <ModalBody>
          <div className={'option-section-text'}>
            <Commonmark
              source={bodyText}/>
          </div>
          <div className={'option-section-text'}>
            <Commonmark
              source={dynamicText}
            />
          </div>
          <Row>
            <Col xs={6}>
              <Button
                block={true}
                className={'admiral-white-btn'}
                onClick={handleOptionRemove(selectedOption.key)}
              >REMOVE COVER</Button>
            </Col>
            <Col xs={6}>
              <Button
                block={true}
                className={'admiral-primary-btn'}
                onClick={closeOptionModal}
              >CLOSE</Button>
            </Col>
          </Row>
        </ModalBody>
      </BootstrapModal>
    )
  }

  return (
    <AdmiralCardV2
      title={'Will you need any extra cover'}
      icon={'shield.svg'}
      collapsable={{
        value: collapsable?.additional,
        onHandleChange: onChangeCollapse,
      }}
      scrollIntoView={{ afterOpened: window.innerHeight <= 800 }}
      styleBody={{ padding: '0' }}
    >
      <Info
        text={<div>If you plan to go on trips which involve a <b>sea
          cruise {showWinterSport && 'or winter sports'}, you must add cover</b>.
          Failure to do so could mean your policy is invalid.</div>}
      />
      <div>
        {boltOns && renderOptions()}
        {boltOns && renderModal()}
        {collapsable && renderContinueButton()}
      </div>
    </AdmiralCardV2>
  )

}
